import { CreditsPlusIcon } from '@components/Icons/CreditsPlusIcon';
import { SECOND } from '@lib/utils/time';
import { formatCreditsWithUnit } from '@lib/utils/units';
import { billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import css from './CreditsBox.module.scss';
import { CreditsBreakdown } from './CreditsBreakdown/CreditsBreakdown';
import { PortalHoverCard } from './PortalHoverCard';

export const CreditsBox = observer(
  ({
    opened,
    anchorDirection = 'above',
  }: {
    opened: boolean;
    anchorDirection?: 'above' | 'below';
  }) => {
    const credits = billingStore.creditsLeft;
    const [open, setOpen] = useState<boolean>(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const closeTimeoutRef = useRef<number | null>(null);

    // Update credits from localStorage when tab is refocused
    // This syncs credits across tabs and is very cheap (just a localStorage read)
    useEffect(() => {
      const handleFocus = () => {
        // Load from localStorage first (instant, no network call)
        billingStore.loadBillingFromLocalStorage();

        // If data is stale (>1 minute old), refresh from server in background
        if (billingStore.isBillingDataStale(60 * SECOND)) {
          billingStore.fetch();
        }
      };

      window.addEventListener('focus', handleFocus);
      return () => window.removeEventListener('focus', handleFocus);
    }, [billingStore]);

    // CreditsBox displays data loaded by other components
    // No need to trigger loading here to avoid duplicate API calls

    const handleMouseEnter = () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      if (!open) {
        trackingStore.creditsHoverOpened({ creditsLeft: credits ?? 0 });
      }
      setOpen(true);

      // Refresh billing data if stale (>1 minute old)
      if (billingStore.isBillingDataStale(60 * SECOND)) {
        billingStore.forceRefreshBillingData();
      }
    };

    const handleMouseLeave = () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = window.setTimeout(() => {
        setOpen(false);
        trackingStore.creditsHoverClosed();
        closeTimeoutRef.current = null;
      }, 120);
    };

    if (credits === null) return null;

    return (
      <div className={classNames(css.creditsBox, opened ? css.stateOpen : css.stateClosed)}>
        <div
          className={classNames(css.creditsInline, open && css.portalOpen)}
          ref={anchorRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span className={css.icon}>
            <CreditsPlusIcon />
          </span>
          <span className={css.creditsLabel}>{formatCreditsWithUnit(credits, 'down')}</span>
        </div>

        <PortalHoverCard
          anchorRef={anchorRef as React.RefObject<HTMLElement>}
          open={open}
          gap={12}
          anchorDirection={anchorDirection}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <CreditsBreakdown showActions={anchorDirection === 'above'} source="credits-hover" />
        </PortalHoverCard>
      </div>
    );
  },
);
